import type { CorruptionTier, Unit, UnitAbility } from "@sins2-atlas/game-data";
import { CORRUPTION_TIERS } from "@sins2-atlas/game-data";
import {
  type Allocation,
  anythingSpent,
  isLegalAllocation,
  joinPointsValues,
  NO_POINTS,
  type PointsValue,
  parsePointsSlot,
  pointedAbilities,
  serializePoints,
  splitPointsSlots,
} from "./ability-points";
import { itemsById } from "./dataset";
import {
  BASE_LEVEL,
  joinLevelValues,
  type LevelValue,
  parseLevelSlot,
  serializeLevel,
  splitLevelSlots,
} from "./level";
import { type Loadout, parseLoadoutSlot, serializeLoadout } from "./loadout";
import { researchIndex } from "./tech-index";
import {
  ALL_TECH_TOKEN,
  EMPTY_TECH_SET,
  maximalKeys,
  parseTechSlot,
  serializeTechParam,
  type TechKey,
  type TechSet,
  tick,
  untick,
} from "./tech-set";
import { joinSlots, SLOT_SEPARATOR, splitSlots } from "./url-slots";

// The stat-frame URL contract (ADR 0002 + Issue 08 + ADR 0007 + ADR 0009 + ADR 0011):
// /ships, /structures, /unit/<id> and /compare carry ?tech= while the research axis is off
// Base, and ?corruption=mild|moderate|extreme while a Corruption Tier is. The frame is a
// PAIR of orthogonal axes (CONTEXT.md: Stat frames, Corruption Tier): the RESEARCH axis and
// the Corruption Tier (uncorrupted / Mild / Moderate / Extreme). Each default stays out of
// the URL so bare links stay canonical.
//
// ?tech= is the ONLY research key (ADR 0011): Base and Fully-Teched were never states
// beside a Tech Set, they are its empty and full extents, so one value says all three —
// empty for Base, the single word `all` for Fully-Teched, and otherwise the set's maximal
// elements (tech-set.ts holds that grammar, url-slots.ts the `;` between columns). ?frame=
// retired with the "Custom wins over fully-teched, and the conflict is reported" special
// case it created: one key cannot contradict itself.
//

// An unknown token still falls back to the axis's default rather than erroring a shared
// link, but the fallback is REPORTED (ADR 0002 amendment 2026-07-30): every page here always
// renders one frame, so a bad token showed default stats under a URL asking for something
// else, with nothing saying so. `none` spelled out is honoured, not reported — it asks
// for exactly what it gets.
//
// This file is the URL contract and the frame TYPE. What the frame does is next door: the
// fold (frame-fold.ts), the words (frame-labels.ts), and the comparison's column-list
// operations (compare.ts). ADR 0012 is held by the type here — SerializedFrame is exactly
// {corruption, tech} — so the split moves nothing that rule depends on.

type ResearchFrame = "base" | "fullyTeched";

/** The Corruption axis: uncorrupted, or one of the game's three tiers. */
export type CorruptionState = "none" | CorruptionTier;

export interface StatFrame {
  research: ResearchFrame;
  corruption: CorruptionState;
  /** The Tech Set the reader has ticked (CONTEXT.md: Tech Set; ADR 0009), or undefined when
   * the research axis is one of its two presets. Present means Custom: the set IS the
   * research selection, so `research` is always "base" beside it and never claims a frame
   * the page is not showing. An empty set is Base, so it is never represented here. */
  tech?: TechSet;
  /** Tokens `?tech=` named that this dataset knows nothing about: dropped from the effective
   * set, reported on the page, and carried on every link out so a stale link survives
   * navigation (the rule unknown comparison ids already follow). */
  techUnknown?: readonly string[];
  /** The Loadout (CONTEXT.md: Loadout; ADR 0012) — the Ship Items equipped on the ONE hull
   * this frame is being read on. Absent when nothing is equipped. Unlike the other two
   * axes it never rides a unit-level link: serializeFrame does not write it, which is what
   * makes clicking to another unit start that hull empty. */
  loadout?: Loadout;
  /** Tokens `?items=` named that this dataset ships no item for: equipped nowhere, reported
   * on the page. */
  loadoutUnknown?: readonly string[];
  /** The hull's Level (CONTEXT.md: Level; ADR 0012, Issue 49) — the second per-hull lever,
   * read on the ONE hull this frame is being read on. Absent IS level 1, which is the Base
   * frame, so a bare link is unchanged. Like the Loadout it never rides a unit-level link:
   * serializeFrame does not write it. */
  level?: number;
  /** The token `?level=` named that is no level the game has (0, 11, "veteran"): the frame
   * falls back to level 1 and the page says so. */
  levelUnknown?: string;
  /** The Ability Points spent on this hull (CONTEXT.md: Ability Points; ADR 0015) — the third
   * per-hull lever, positional over the hull's level-driven abilities in the game's order.
   * Absent IS nothing spent, which is the Base frame, so a bare link is unchanged. Like the
   * Loadout and the Level it never rides a unit-level link: serializeFrame does not write it. */
  points?: Allocation;
  /** The `?points=` value that is no allocation the grammar admits ("two", "1.5", "0x1"): the
   * frame falls back to nothing spent and the page says so. The whole value, because one bad
   * token makes the whole positional list unreadable. */
  pointsUnknown?: string;
}

export const BASE_FRAME: StatFrame = { research: "base", corruption: "none" };

const UNCORRUPTED_TOKEN = "none";

/** The corruption tokens the URL may spell: the game's three adjectives as ids, plus the
 * spelled-out default. */
const CORRUPTION_TOKENS: readonly string[] = [UNCORRUPTED_TOKEN, ...CORRUPTION_TIERS];

function isTier(value: string): value is CorruptionTier {
  return (CORRUPTION_TIERS as readonly string[]).includes(value);
}

/** The URL keys the frame lives in: one per axis. */
export interface FrameSearch {
  corruption?: string;
  tech?: string;
  /** The Loadout's key (ADR 0012). Read here, never written by serializeFrame. */
  items?: string;
  /** The Level's key (ADR 0012, Issue 49). Read here, never written by serializeFrame. It is
   * the site's one numeric key, so the router hands a single level over as a number and a
   * `;`-slotted comparison value as a string (lib/level.ts). */
  level?: LevelValue;
  /** The Ability Points key (ADR 0015, Issue 60). Read here, never written by serializeFrame.
   * Numeric-or-string for the reason `level` is: a one-position allocation is a bare number
   * to the router's JSON.parse (lib/ability-points.ts). */
  points?: PointsValue;
}

const knownItem = (id: string) => itemsById.has(id);

export function parseFrame(search: FrameSearch): StatFrame {
  const corruption = search.corruption;
  const { all, set, unknown } = parseTechSlot(search.tech, researchIndex);
  const loadout = parseLoadoutSlot(search.items, knownItem);
  const level = parseLevelSlot(search.level);
  const points = parsePointsSlot(search.points);
  const custom = set.size > 0;
  return {
    research: all ? "fullyTeched" : "base",
    corruption: corruption !== undefined && isTier(corruption) ? corruption : "none",
    ...(custom ? { tech: set } : {}),
    ...(unknown.length > 0 ? { techUnknown: unknown } : {}),
    ...(loadout.items.size > 0 ? { loadout: loadout.items } : {}),
    ...(loadout.unknown.length > 0 ? { loadoutUnknown: loadout.unknown } : {}),
    ...(level.level === BASE_LEVEL ? {} : { level: level.level }),
    ...(level.unknown === undefined ? {} : { levelUnknown: level.unknown }),
    ...(anythingSpent(points.points) ? { points: points.points } : {}),
    ...(points.unknown === undefined ? {} : { pointsUnknown: points.unknown }),
  };
}

/** The frame a ROSTER view reads: the empire-wide levers only (ADR 0012 — a Loadout belongs
 * to one hull in one place, and a roster row is 166 hulls at once).
 *
 * A view's own Zod schema does not settle this. TanStack Router unions each route's search
 * with its ancestors', and the root route validates nothing, so every key in the URL reaches
 * every page whether its schema declares it or not — `/ships?items=trader_missile_armor`
 * reached the Kol's row and read Armor Strength 155 and 3,000 credits there (measured on the
 * served build 2026-09-07). The exclusion is a rule, so it is stated once, here, rather than
 * left to each roster surface to remember. */
export function parseRosterFrame(search: FrameSearch): StatFrame {
  return parseFrame({ ...search, items: undefined, level: undefined, points: undefined });
}
/** The union of a per-slot reader over every `;` slot of a comparison value (ADR 0011),
 * deduped in URL order — the four *InSlots readers below are this over their own splitter,
 * ready before the comparison needs them (Issue 52). */
function acrossSlots(slots: readonly string[], of: (slot: string) => Iterable<string>): string[] {
  return [...new Set(slots.flatMap((slot) => [...of(slot)]))];
}

/** What `?items=` named that this dataset ships no item for, for the notice to quote back.
 * Parsing still equips the rest, exactly as an unknown `?tech=` token folds nothing. */
export function unknownLoadoutTokens(value: string | undefined): readonly string[] {
  return parseLoadoutSlot(value, knownItem).unknown;
}

/** The same across every `?items=` slot (ADR 0011's `;` grammar), deduped in URL order —
 * the comparison's reader, ready before the comparison needs it (Issue 52). */
export function unknownLoadoutTokensInSlots(value: string | undefined): readonly string[] {
  return acrossSlots(splitSlots(value), unknownLoadoutTokens);
}

/** What `?level=` named that is no level the game has, for the notice to quote back. Parsing
 * still reads the hull at level 1, exactly as an unknown `?tech=` token folds nothing. */
export function unknownLevelToken(value: LevelValue | undefined): string | undefined {
  return parseLevelSlot(value).unknown;
}

/** The same across every `?level=` slot (ADR 0011's `;` grammar), deduped in URL order — the
 * comparison's reader, ready before the comparison needs it (Issue 52). */
export function unknownLevelTokensInSlots(value: LevelValue | undefined): readonly string[] {
  return acrossSlots(splitLevelSlots(value), (slot) => {
    const token = unknownLevelToken(slot);
    return token === undefined ? [] : [token];
  });
}

/** The Level as its own URL key — deliberately NOT part of SerializedFrame, for the reason
 * `?items=` is not (ADR 0012: a Level is something one ship earned, so it does not ride
 * unit-level links). An unrecognised token rides back out verbatim so the page keeps
 * reporting it, the rule `?tech=` already follows. */
export function serializeLevelSearch(frame: StatFrame): { level: LevelValue | undefined } {
  return { level: frame.levelUnknown ?? serializeLevel(frame.level) };
}

/** What `?points=` named that is no allocation the grammar admits, for the notice to quote
 * back. Parsing still reads the hull with nothing spent, exactly as an unknown `?tech=` token
 * folds nothing. */
export function unknownPointsToken(value: PointsValue | undefined): string | undefined {
  return parsePointsSlot(value).unknown;
}

/** The same across every `?points=` slot (ADR 0011's `;` grammar), deduped in URL order — the
 * comparison's reader, ready before the comparison needs it (Issue 63). */
export function unknownPointsTokensInSlots(value: PointsValue | undefined): readonly string[] {
  return acrossSlots(splitPointsSlots(value), (slot) => {
    const token = unknownPointsToken(slot);
    return token === undefined ? [] : [token];
  });
}

/** The allocation as its own URL key — deliberately NOT part of SerializedFrame, for the
 * reason `?items=` and `?level=` are not (ADR 0015 inherits ADR 0012's rule: Ability Points
 * are something one ship bought, so they do not ride unit-level links). An unrecognised value
 * rides back out verbatim so the page keeps reporting it. */
export function serializePointsSearch(frame: StatFrame): { points: PointsValue | undefined } {
  return { points: frame.pointsUnknown ?? serializePoints(frame.points) };
}

/** The frame carrying this allocation — the one write the Ability Points control makes
 * (Issue 62). Nothing spent leaves the frame with no allocation at all, which is what keeps a
 * bare link bare; and spending anything drops a `?points=` value that was no allocation,
 * because the reader has just said what they meant and the page must stop quoting the old
 * mistake back at them (serializePointsSearch prefers the unknown value). */
export function withPoints(frame: StatFrame, points: Allocation): StatFrame {
  const { points: _points, pointsUnknown: _pointsUnknown, ...rest } = frame;
  return anythingSpent(points) ? { ...rest, points } : rest;
}

/** The allocation the URL names that this hull cannot reach: one naming more abilities than
 * the hull has level-driven ones, one putting an Ability Level above what the frame's Level
 * permits, or one spending more than the pool (ADR 0015 decision 2). Returned as the
 * allocation for the page to quote back; undefined when nothing is spent or when the hull
 * could field exactly what was asked for.
 *
 * A hull with no experience ladder has no level-driven abilities at all, so any allocation on
 * one is unreachable and says so through the same sentence. */
export function unhonouredPoints(
  unit: { abilities: readonly UnitAbility[] },
  frame: StatFrame,
): Allocation | undefined {
  const points = frame.points;
  if (points === undefined || points.length === 0) return undefined;
  const abilities = pointedAbilities(unit.abilities);
  return isLegalAllocation(points, abilities, frame.level ?? BASE_LEVEL) ? undefined : points;
}

/** The allocation the page is actually SHOWING: the frame's own when this hull could have
 * bought it, and nothing spent when it could not. The fold reads a `?points=` value the same
 * way (frame-fold.ts, boughtAbilityLevels) — an unreachable allocation folds nothing and is
 * quoted back by the notice — so the trigger's count, the panel's positions and the stat
 * cells all read one allocation rather than three (Issue 62). */
export function honouredPoints(
  unit: { abilities: readonly UnitAbility[] },
  frame: StatFrame,
): Allocation {
  return unhonouredPoints(unit, frame) === undefined ? (frame.points ?? NO_POINTS) : NO_POINTS;
}

/** The frame with this hull moved to that Level (Issue 50) — the one write the Level
 * control makes. Two rules live here rather than in the component: level 1 leaves the frame
 * with no level at all, which is what keeps a bare link bare; and choosing ANY level drops a
 * `?level=` token that was no level, because the reader has just said what they meant and the
 * page must stop quoting the old mistake back at them (serializeLevelSearch prefers the
 * unknown token, so leaving it on would write ?level=veteran and read level 1). */
export function withLevel(frame: StatFrame, level: number): StatFrame {
  // The rest keeps every other axis: a Level is orthogonal to research, Corruption and the
  // Loadout, and choosing one must not disturb any of them.
  const { level: _level, levelUnknown: _levelUnknown, ...rest } = frame;
  return level === BASE_LEVEL ? rest : { ...rest, level };
}

/** The Loadout as its own URL key — deliberately NOT part of SerializedFrame (ADR 0012:
 * a Loadout is something one ship carries, so it does not ride unit-level links). The one
 * surface that writes it is the hull's own page, which spreads it over the URL it is
 * already on. */
export function serializeLoadoutSearch(frame: StatFrame): { items: string | undefined } {
  const known = serializeLoadout(frame.loadout);
  const tokens = [...(known === undefined ? [] : [known]), ...(frame.loadoutUnknown ?? [])];
  return { items: tokens.length === 0 ? undefined : tokens.join(",") };
}

/** The frame with both per-hull levers cleared (Issue 52, ADR 0012): the empire-wide axes
 * exactly as they were, no Loadout and level 1. A Loadout and a Level belong to ONE hull in
 * one place, so a hull arriving somewhere new starts bare of both — which is what makes
 * adding a unit to a comparison, from the tray, the palette, a roster tick or an entity-page
 * button, mean the same thing however it was done. The reported tokens go with the levers
 * they belong to: a frame carrying neither has nothing left to quote back. */
export function bareHull(frame: StatFrame): StatFrame {
  const {
    loadout: _loadout,
    loadoutUnknown: _loadoutUnknown,
    level: _level,
    levelUnknown: _levelUnknown,
    points: _points,
    pointsUnknown: _pointsUnknown,
    ...rest
  } = frame;
  return rest;
}

/** Whether the research axis is a ticked Tech Set rather than one of the two presets. */
export function isCustomTech(frame: StatFrame): boolean {
  return frame.tech !== undefined;
}

// The Tech control's two extents (ADR 0011, Issue 38). The retired segmented control's
// "Base / Fully-Teched / Custom" was never three states on this axis: Base is the empty
// Tech Set and Fully-Teched is the full one, which is why the dropdown's Deselect all and
// Select all say the same thing in the set's own words. Both drop the ticked set rather
// than leave it riding invisibly in the URL, and neither touches the Corruption axis
// (ADR 0007: it is orthogonal, and its own control).

/** Select all: the set's full extent. The unknown tokens go too — the slot then spells
 * `all`, which is the WHOLE value, so there is nowhere left to carry them. */
export function selectAllTech(frame: StatFrame): StatFrame {
  return { ...frame, research: "fullyTeched", tech: undefined, techUnknown: undefined };
}

/** Deselect all: the empty set, which is Base. The unknown tokens go too. A stale token
 * rides every link out while nobody has touched the control — that is how the notice keeps
 * reporting it — but Deselect all is a statement about the WHOLE slot, exactly as Select all
 * is, so it empties the slot rather than leaving `?tech=bogus` behind an empty set with the
 * notice still up. */
export function deselectAllTech(frame: StatFrame): StatFrame {
  return { ...frame, research: "base", tech: undefined, techUnknown: undefined };
}

/** The frame with one key ticked or unticked, closed by the game's own requirement rules
 * (tech-set.ts). An empty result drops back to Base, because an empty Tech Set IS Base. */
export function toggleTech(frame: StatFrame, key: TechKey, on: boolean): StatFrame {
  // Under the full extent there is nothing to toggle: `all` is a value, not a listed
  // membership (tech-set.ts), so "everything but this one" has no URL to be written in.
  // The frame comes back untouched and Deselect all is how a reader leaves it — the one
  // guard every ticking surface (the picker's boxes, the tree's tier buttons and its
  // Selected Subject panel) shares, since all three toggle through here.
  if (frame.tech === undefined && frame.research === "fullyTeched") return frame;
  const current = frame.tech ?? EMPTY_TECH_SET;
  const next = on ? tick(current, key, researchIndex) : untick(current, key, researchIndex);
  return { ...frame, research: "base", tech: next.size === 0 ? undefined : next };
}

/** The set's maximal elements: the keys the reader ticked themselves, as opposed to the
 * ones the closure pulled in. The tick list marks the difference so a reader can see what
 * a tick required, which is the game's own rule made visible. */
export function maximalTechKeys(frame: StatFrame): ReadonlySet<TechKey> {
  if (frame.tech === undefined) return new Set<TechKey>();
  return new Set(maximalKeys(frame.tech, researchIndex));
}

/** How one key sits in the set: the reader's own tick, one the closure required, or the
 * full extent — under Fully-Teched every key is in the set because `all` says so, and no
 * key is individually listed. `teched` is separate from `ticked` because the difference is
 * what a surface may offer: a ticked key can be unticked, while the full extent has no
 * all-but-one to serialise (tech-set.ts: `all` is a value, never a membership), so a row
 * under it is stated, not offered. */
export type TechMark = "ticked" | "implied" | "teched";

/** The set's marks as a lookup, built once per frame: "ticked" for a key the reader ticked
 * themselves (a maximal element), "implied" for one the closure pulled in, undefined for a
 * key the set does not hold at all. Surfaces that show the set on something other than a
 * checkbox — the tree's subject cells and tier buttons (Issue 27) — mark the two apart,
 * because what put the implied ones there is the game's own requirement rule, and a reader
 * who cannot see the difference cannot tell what their one tick actually bought. Returned
 * as a closure rather than a per-key function so a tree of 139 cells walks the set once. */
export function techMarker(frame: StatFrame): (key: TechKey) => TechMark | undefined {
  const set = frame.tech;
  if (set === undefined) {
    return frame.research === "fullyTeched" ? () => "teched" : () => undefined;
  }
  const own = new Set(maximalKeys(set, researchIndex));
  return (key) => (set.has(key) ? (own.has(key) ? "ticked" : "implied") : undefined);
}

/** The `tech` tokens this dataset knows nothing about, for the notice to quote back. Reads
 * through parseTechSlot — the same door parseFrame reads through — because the two disagree
 * on exactly one value: the whole-slot word `all`, which IS Fully-Teched and names nothing
 * only inside a comma list. Reporting through the token parser alone quoted a reader's own
 * `?tech=all` back at them as a token naming nothing. */
export function unknownTechTokens(value: string | undefined): readonly string[] {
  return parseTechSlot(value, researchIndex).unknown;
}

/** The token the URL's corruption axis named that the game does not. Reporting is all
 * this decides — parseFrame still falls back to uncorrupted. */
export function unknownCorruptionToken(value: string | undefined): string | undefined {
  if (value === undefined || value === "") return undefined;
  return CORRUPTION_TOKENS.includes(value) ? undefined : value;
}

/** The pair as URL search keys, both always present (undefined = default) — a component
 * that forwards the frame on a link types its prop as this, so leaving an axis out is a
 * compile error, not a silently dropped tier (review round 2026-09-05). One key per axis
 * since ADR 0011: the research axis writes `tech` and nothing else. */
export interface SerializedFrame {
  corruption: string | undefined;
  tech: string | undefined;
}

/** The research axis as its one slot value: undefined for Base, `all` for Fully-Teched,
 * else the set's maximal elements (tech-set.ts) — the URL never spells a prerequisite or
 * a Tier the set already implies. Unknown tokens ride after them, in the order they
 * arrived, so a link keeps saying what it said. */
function serializeResearch(frame: StatFrame): string | undefined {
  if (frame.tech === undefined && frame.research === "fullyTeched") return ALL_TECH_TOKEN;
  const set = frame.tech === undefined ? undefined : serializeTechParam(frame.tech, researchIndex);
  const tokens = [...(set === undefined ? [] : [set]), ...(frame.techUnknown ?? [])];
  return tokens.length === 0 ? undefined : tokens.join(",");
}

export function serializeFrame(frame: StatFrame): SerializedFrame {
  return {
    corruption: frame.corruption === "none" ? undefined : frame.corruption,
    tech: serializeResearch(frame),
  };
}

/** The stat frame as it rides a SHELL-level link — the nav tabs, which sit above any one
 * route and so read the search loosely. Parsed and re-serialized, exactly as
 * `factionLinkSearch` does for the Faction Selection, so a canonical link stays short and
 * the reader's research follows them from /tech to /ships without being retyped.
 *
 * A /compare URL carries one slot per column (ADR 0011) and no single-frame page can honour
 * a list, so a multi-slot value rides NOTHING rather than arriving as column one's frame
 * wearing the whole matchup's clothes. */
export function frameLinkSearch(search: FrameSearch): SerializedFrame {
  return serializeFrame(linkFrame(search));
}

/** The frame a SHELL-level link reads off the search it is leaving — frameLinkSearch before
 * it is serialised, for a caller that composes a whole unit-level link from it (the palette,
 * lib/unit-link.ts). A multi-slot comparison value reads as Base, for the reason above; Base
 * serialises to the two absent keys that rule used to write directly. */
export function linkFrame(search: FrameSearch): StatFrame {
  const multi = (value: string | undefined) => value?.includes(SLOT_SEPARATOR) === true;
  if (multi(search.tech) || multi(search.corruption)) return BASE_FRAME;
  return parseFrame(search);
}

// The per-column layer (ADR 0011). A comparison shows one unit per column and a column is a
// unit under its OWN stat frame, so on /compare each URL key carries one slot per column
// (url-slots.ts owns the `;` split). parseFrames / serializeFrames are the list twins of
// parseFrame / serializeFrame and must never drift from them, which is why they live here
// rather than in a module of their own: a one-slot value parses and serializes byte-identically
// through either pair, and that is a property one file can hold true.

/** The frames the URL's slots name, in column order. An absent value names no slots, so a
 * comparison with no frame keys reads as an empty list and every column falls back to Base
 * (frameAt). The two keys are independent lists: a matchup may set Corruption on three
 * columns and Tech on one, so the count is the longer of the two. */
export function parseFrames(search: FrameSearch): StatFrame[] {
  const tech = splitSlots(search.tech);
  const corruption = splitSlots(search.corruption);
  // `?items=` takes the same slot grammar (ADR 0012 + ADR 0011): a Loadout belongs to ONE
  // hull, and a comparison column is one hull, so a column may carry one.
  const items = splitSlots(search.items);
  // `?level=` takes the same slot grammar (ADR 0012 + ADR 0011), for the same reason: a Level
  // belongs to ONE hull, and a comparison column is one hull.
  const level = splitLevelSlots(search.level);
  // `?points=` takes the same slot grammar (ADR 0015 + ADR 0011), for the same reason: an
  // allocation belongs to ONE hull, and a comparison column is one hull.
  const points = splitPointsSlots(search.points);
  const count = Math.max(tech.length, corruption.length, items.length, level.length, points.length);
  return Array.from({ length: count }, (_, index) =>
    parseFrame({
      tech: tech[index],
      corruption: corruption[index],
      items: items[index],
      level: level[index],
      points: points[index],
    }),
  );
}

/** The column frames as URL keys: each axis's slots joined, trailing defaults trimmed. One
 * frame in gives exactly what serializeFrame gives, which is what keeps every single-unit
 * link unchanged in shape. */
export function serializeFrames(frames: readonly StatFrame[]): SerializedFrame {
  const slots = frames.map(serializeFrame);
  return {
    corruption: joinSlots(slots.map((slot) => slot.corruption)),
    tech: joinSlots(slots.map((slot) => slot.tech)),
  };
}

/** Every URL key a COMPARISON writes (Issue 52): the two that ride links, plus the two
 * per-hull levers that do not. It extends SerializedFrame rather than widening it, because
 * the narrow type is what holds ADR 0012's rule by construction — a component that forwards a
 * frame along a link types its prop as SerializedFrame and so cannot carry these two. A
 * comparison column is not a link: it is a hull in one place, exactly as an entity page is,
 * which is why /compare writes all four and every unit-level link writes two. */
interface SerializedColumns extends SerializedFrame {
  items: string | undefined;
  level: LevelValue | undefined;
  points: PointsValue | undefined;
}

/** The column frames as the comparison's own four keys. Each axis's slots are joined with
 * trailing defaults trimmed (url-slots.ts owns that rule), so one column writes byte-identically
 * to what the hull's own page writes and a bare matchup writes a bare link. */
export function serializeColumns(frames: readonly StatFrame[]): SerializedColumns {
  return {
    ...serializeFrames(frames),
    items: joinSlots(frames.map((frame) => serializeLoadoutSearch(frame).items)),
    level: joinLevelValues(frames.map((frame) => serializeLevelSearch(frame).level)),
    points: joinPointsValues(frames.map((frame) => serializePointsSearch(frame).points)),
  };
}

/** What `?tech=` named across ALL the columns that this dataset knows nothing about, deduped
 * in URL order. The single-slot twin cannot serve a comparison: a legal `all;bogus` is one
 * string, so it read as one unrecognised token and the notice quoted the reader's own good
 * slot back at them. */
export function unknownTechTokensInSlots(value: string | undefined): readonly string[] {
  return acrossSlots(splitSlots(value), unknownTechTokens);
}

/** The same for `?corruption=`: every slot naming a Corruption the game does not, deduped in
 * URL order. Each such slot falls back to uncorrupted, exactly as a single-frame page's does. */
export function unknownCorruptionTokensInSlots(value: string | undefined): readonly string[] {
  return acrossSlots(splitSlots(value), (slot) => {
    const token = unknownCorruptionToken(slot);
    return token === undefined ? [] : [token];
  });
}

/** The Level the URL names that the opened unit cannot reach — a frigate, a structure or a
 * strikecraft has no experience ladder, so nothing on its page moves. Returned as the level
 * for the page to say so; undefined at level 1 or on a hull that has levels. */
export function unhonouredLevel(unit: Unit, frame: StatFrame): number | undefined {
  if (unit.leveled || frame.level === undefined) return undefined;
  return frame.level;
}

/** The Loadout the URL names that the opened unit cannot carry — an item id this dataset
 * ships but this hull's shop does not list. Returned as the ids for the page to quote back;
 * empty when everything named is buyable here. */
export function unhonouredLoadout(unit: Unit, frame: StatFrame): readonly string[] {
  if (frame.loadout === undefined) return [];
  return [...frame.loadout].filter((id) => !unit.items.includes(id));
}
